import { createSlice, ActionReducerMapBuilder, PayloadAction } from "@reduxjs/toolkit";
import { FeedManagerState, ProcessedData } from "./types";
import { fetchData } from "./asyncThunks";
import { FEED_MANAGER_INITIAL_STATE } from "./config";
import { sortSecurities } from "./utils";
import { Environment } from "../../../service/globalTypes";
import { setAllQuoteFeedsAsNone } from "../../../service/producerServices/setAllQuoteFeedsAsNone";

const initialState: FeedManagerState = {
  token: null,
  errorValidatingToken: false,
  isLoading: false,
  data: FEED_MANAGER_INITIAL_STATE,
  isAutoUpdate: false,
  environment: { current: Environment.PRODUCTION, last: null },
  lastValues: {},
};

const options = {
  name: "feedManager",
  initialState,
  reducers: {
    setToken: (state: FeedManagerState, action: PayloadAction<string | null>) => {
      state.token = action.payload;
    },
    removeToken: (state: FeedManagerState) => {
      state.token = null;
    },
    setAutoUpdate: (state: FeedManagerState, action: PayloadAction<boolean>) => {
      state.isAutoUpdate = action.payload;
    },
    addToLastValues: (state: FeedManagerState, action: PayloadAction<{ cell: string; value: string }>) => {
      state.lastValues[action.payload.cell] = {
        value: action.payload.value,
      };
    },
    setEnvironment: (state: FeedManagerState, action: PayloadAction<Environment>) => {
      state.environment.current = action.payload;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<FeedManagerState>) => {
    builder.addCase(fetchData.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchData.fulfilled, (state, action) => {
      const { principal, securities, quote_feeds } = action.payload.data;
      const { current: currentEnvironment, last: lastEnvironment } = state.environment;

      const sortedSecurities = sortSecurities(securities);

      state.data = {
        principal,
        securities: sortedSecurities,
        quote_feeds,
      } as ProcessedData;

      state.token = action.payload.token;
      state.errorValidatingToken = false;
      state.isLoading = false;

      if (lastEnvironment !== null && currentEnvironment !== lastEnvironment) {
        setAllQuoteFeedsAsNone();
      }

      state.environment.last = currentEnvironment;
    });
    builder.addCase(fetchData.rejected, (state) => {
      state.errorValidatingToken = true;
      state.data = FEED_MANAGER_INITIAL_STATE;
      state.isLoading = false;
    });
  },
};

const feedManagerSlice = createSlice(options);

export const { setToken, removeToken, setAutoUpdate, addToLastValues, setEnvironment } = feedManagerSlice.actions;

export const selectSecurities = (state: { feedManager: FeedManagerState }) => state.feedManager.data.securities;
export const selectQuoteFeeds = (state: { feedManager: FeedManagerState }) => state.feedManager.data.quote_feeds;
export const selectToken = (state: { feedManager: FeedManagerState }) => state.feedManager.token;
export const selectIsErrorValidatingToken = (state: { feedManager: FeedManagerState }) =>
  state.feedManager.errorValidatingToken;
export const selectIsLoading = (state: { feedManager: FeedManagerState }) => state.feedManager.isLoading;
export const selectData = (state: { feedManager: FeedManagerState }) => state.feedManager.data;
export const selectAutoUpdateEnabled = (state: { feedManager: FeedManagerState }) => state.feedManager.isAutoUpdate;
export const selectLastValues = (state: { feedManager: FeedManagerState }) => state.feedManager.lastValues;

export default feedManagerSlice.reducer;
